import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ProductCard } from "@/components/product-card";
import { ProductSkeleton } from "@/components/skeleton-card";
import { fetchProducts, type Product } from "@/lib/queries";
import { Reveal } from "@/components/reveal";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { VerifiedBadge } from "@/components/verified-badge";

const CATEGORY_META: Record<
  string,
  {
    title: string;
    description: string;
    keywords: string;
    display: string;
    dbCategories?: string[];
  }
> = {
  "womens-fashion": {
    title: "Women's Fashion | Shop Creator Curations | Pluggz",
    description:
      "Discover women's fashion curated by your favourite creators. Shop influencer outfits, date night looks, and capsule wardrobe essentials on Pluggz.",
    keywords:
      "women's fashion, shop influencer outfits, date night outfits, capsule wardrobe, curated fashion marketplace, creator fashion picks, what influencer wore",
    display: "Women's Fashion",
    dbCategories: ["Womens Fashion"],
  },
  "beauty-skincare": {
    title: "Beauty & Skincare | Shop Creator Curations | Pluggz",
    description:
      "Shop beauty and skincare products hand-picked by vetted creators. Discover the routines and products influencers actually use on Pluggz.",
    keywords:
      "beauty skincare, influencer beauty routine, creator skincare picks, shop beauty products, curated beauty marketplace, skincare essentials",
    display: "Beauty & Skincare",
    dbCategories: ["Beauty & Skincare"],
  },
  "mother-baby": {
    title: "Mother & Baby | Shop Creator Curations | Pluggz",
    description:
      "Discover mother and baby essentials curated by parenting creators. Shop trusted products for mums and little ones on Pluggz.",
    keywords:
      "mother baby products, parenting essentials, mum must-haves, baby care creator picks, family lifestyle, newborn essentials",
    display: "Mother & Baby",
    dbCategories: ["Mother & Baby"],
  },
  "home-decor": {
    title: "Home & Decor | Shop Creator Curations | Pluggz",
    description:
      "Transform your space with home and decor products curated by lifestyle creators. Shop affordable home decor and interior inspiration on Pluggz.",
    keywords:
      "home decor, affordable home decor, interior inspiration, creator home picks, lifestyle home products, home styling",
    display: "Home & Decor",
    dbCategories: ["Home & Decor"],
  },
  "fitness-wellness": {
    title: "Fitness, Wellness & Healthy Lifestyle | Shop Creator Curations | Pluggz",
    description:
      "Shop fitness, wellness and healthy lifestyle products curated by creators. Discover what your favourite wellness influencers actually use on Pluggz.",
    keywords:
      "fitness wellness, healthy lifestyle, wellness influencer picks, workout essentials, self care products, activewear",
    display: "Fitness, Wellness & Healthy Lifestyle",
    dbCategories: ["Fitness & Wellness"],
  },
  "shoes-accessories": {
    title: "Shoes & Accessories | Shop Creator Curations | Pluggz",
    description:
      "Discover shoes and accessories hand-picked by fashion creators. Shop the bags, jewellery and footwear influencers love on Pluggz.",
    keywords:
      "shoes accessories, shop influencer accessories, designer dupes, bags jewellery footwear, fashion accessories, statement pieces",
    display: "Shoes & Accessories",
    dbCategories: ["Shoes & Accessories"],
  },
  "cooking-grocery": {
    title: "Cooking & Grocery Hauls | Shop Creator Curations | Pluggz",
    description:
      "Discover cooking essentials and grocery favourites curated by food creators. Shop what influencers are cooking with on Pluggz.",
    keywords:
      "cooking grocery hauls, kitchen essentials, food creator picks, cooking influencer, grocery favourites, meal prep",
    display: "Cooking & Grocery Hauls",
    dbCategories: ["Cooking & Grocery"],
  },
  "finance-lifestyle": {
    title: "Finance - Smart Lifestyle & Investments | Shop Creator Curations | Pluggz",
    description:
      "Explore finance, smart lifestyle and investment picks curated by creators. Shop books, tools and resources for building wealth on Pluggz.",
    keywords:
      "finance lifestyle, smart investments, wealth building, financial literacy, money mindset creator picks, passive income",
    display: "Finance — Smart Lifestyle & Investments",
    dbCategories: ["Finance & Lifestyle"],
  },
  travel: {
    title: "Travel With Me | Shop Creator Curations | Pluggz",
    description:
      "Shop travel essentials and adventure gear curated by travel creators. Discover packing must-haves and wanderlust favourites on Pluggz.",
    keywords:
      "travel essentials, adventure gear, packing must haves, travel influencer picks, wanderlust favourites, travel accessories",
    display: "Travel with Me",
    dbCategories: ["Travel"],
  },
  grwm: {
    title: "GRWM - Get Ready With Me | Shop Creator Curations | Pluggz",
    description:
      "Shop GRWM favourites — the makeup, skincare and fashion pieces creators use in their get-ready routines. Discover influencer must-haves on Pluggz.",
    keywords:
      "GRWM, get ready with me, makeup routine, creator GRWM favourites, influencer daily essentials, morning routine",
    display: "GRWM — Get Ready With Me",
    dbCategories: ["GRWM"],
  },
};

const VALID_SLUGS = Object.keys(CATEGORY_META);

export const Route = createFileRoute("/category/$slug")({
  head: ({ params }) => {
    const meta = CATEGORY_META[params.slug];
    if (!meta) {
      return {
        meta: [{ title: "Not Found | Pluggz" }],
        links: [{ rel: "canonical", href: `${SITE_URL}/discover` }],
      };
    }
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { name: "keywords", content: meta.keywords },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:url", content: `${SITE_URL}/category/${params.slug}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
      ],
      links: [
        { rel: "canonical", href: `${SITE_URL}/category/${params.slug}` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: meta.display,
            description: meta.description,
            url: `${SITE_URL}/category/${params.slug}`,
          }),
        },
      ],
    };
  },
  component: CategoryPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-7xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Category not found</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        Browse our full discovery feed instead.
      </p>
      <Link
        to="/discover"
        className="mt-8 inline-flex items-center gap-2 border border-foreground px-5 py-2.5 text-xs uppercase tracking-widest transition-colors hover:bg-foreground hover:text-background"
      >
        Discover products
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  ),
});

function CategoryPage() {
  const { slug } = Route.useParams();
  const meta = CATEGORY_META[slug];

  if (!meta || !VALID_SLUGS.includes(slug)) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h1 className="font-serif text-4xl">Category not found</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Browse our full discovery feed instead.
        </p>
        <Link
          to="/discover"
          className="mt-8 inline-flex items-center gap-2 border border-foreground px-5 py-2.5 text-xs uppercase tracking-widest transition-colors hover:bg-foreground hover:text-background"
        >
          Discover products
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: fetchProducts,
  });

  const dbCats = meta.dbCategories ?? [meta.display];
  const filtered = products.filter((p: Product) => {
    const cat = (p.category ?? "").toLowerCase();
    return dbCats.some(
      (c) =>
        cat === c.toLowerCase() ||
        cat.includes(c.toLowerCase()) ||
        c.toLowerCase().includes(cat)
    );
  });

  const { data: categoryCreators = [] } = useQuery({
    queryKey: ["category-creators", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creator_products")
        .select("creators!inner(id, handle, name, avatar_url, is_verified), products!inner(category)")
        .in("products.category", dbCats)
        .eq("creators.status", "live")
        .limit(40);
      if (error) return [];
      const seen = new Set<string>();
      const out: { id: string; handle: string; name: string; avatar_url: string | null; is_verified: boolean | null }[] = [];
      for (const row of (data ?? []) as any[]) {
        const c = Array.isArray(row.creators) ? row.creators[0] : row.creators;
        if (!c || seen.has(c.id)) continue;
        seen.add(c.id);
        out.push({ id: c.id, handle: c.handle, name: c.name, avatar_url: c.avatar_url, is_verified: c.is_verified });
        if (out.length >= 8) break;
      }
      return out;
    },
    staleTime: 10 * 60 * 1000,
  });

  const [visible, setVisible] = useState(24);
  const visibleProducts = filtered.slice(0, visible);

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${meta.display} on Pluggz`,
    itemListElement: visibleProducts.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}/product/${p.id}`,
      name: p.title,
    })),
  };

  return (
    <div className="mx-auto max-w-7xl px-6 pt-12 pb-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }}
      />
      <Reveal>
        <p className="editorial-eyebrow">Category</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl">{meta.display}</h1>
        <p className="mt-4 max-w-xl text-sm text-muted-foreground">
          {meta.description}
        </p>
        <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? "product" : "products"} curated by {categoryCreators.length} {categoryCreators.length === 1 ? "creator" : "creators"}
        </p>
      </Reveal>

      {categoryCreators.length > 0 && (
        <section className="mt-10">
          <p className="editorial-eyebrow mb-4">Creators in {meta.display}</p>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {categoryCreators.map((c) => (
              <Link
                key={c.id}
                to="/creator/$handle"
                params={{ handle: c.handle }}
                className="flex w-24 shrink-0 flex-col items-center gap-2 group"
              >
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name} className="h-16 w-16 rounded-full object-cover transition-transform group-hover:scale-105" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-muted" />
                )}
                <div className="text-center">
                  <p className="font-serif text-xs leading-tight flex items-center justify-center gap-1">
                    {c.name}
                    {c.is_verified && <VerifiedBadge />}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">@{c.handle}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
        {isLoading ? (
          <>
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
          </>
        ) : (
          visibleProducts.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 90}>
              <ProductCard
                product={p}
                creatorHandle={p.creator_handle}
                creatorName={p.creator_name}
              />
            </Reveal>
          ))
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="col-span-full">
            <Reveal>
              <div className="mx-auto max-w-md py-24 text-center">
                <p className="editorial-eyebrow">Coming soon</p>
                <h2 className="mt-3 font-serif text-4xl">
                  Nothing here yet
                </h2>
                <p className="mt-4 text-sm text-muted-foreground">
                  Products for {meta.display} will appear here once creators go
                  live.
                </p>
                <Link
                  to="/creators"
                  className="mt-8 inline-flex items-center border border-foreground/80 px-5 py-2.5 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-foreground hover:text-background"
                >
                  Meet the creators
                </Link>
              </div>
            </Reveal>
          </div>
        )}
      </div>

      {!isLoading && filtered.length > visible && (
        <div className="mt-12 flex justify-center">
          <button
            onClick={() => setVisible((v) => v + 24)}
            className="inline-flex items-center gap-2 border border-foreground px-6 py-3 text-xs uppercase tracking-widest transition-colors hover:bg-foreground hover:text-background"
          >
            Load more
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
